/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type { ServerRequestV1 } from '@feature-hub/server-request';
import type { ContentServiceV1 as ContentService } from '@volkswagen-onehub/audi-content-service';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { PageInfoServiceV1 } from '@oneaudi/page-info-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { HelmetProvider } from 'react-helmet-async';
import { v4 as uuidv4 } from 'uuid';
import App from './FeatureApp';
import type { GetFilteredVehiclesResponse } from './types';

interface GetVehicleInfoForWormwoodResponse {
  data?: { getVehicleInfoForWormwood?: { vin?: string } };
}

interface GetFilteredVehiclesForWormwoodResponse {
  data?: GetFilteredVehiclesResponse;
}

declare global {
  interface Window {
    __SSR_GRAPHQL_DATA__: {
      success: boolean;
      data?: GetVehicleInfoForWormwoodResponse | GetFilteredVehiclesForWormwoodResponse;
      timestamp: number;
      error?: string;
      vehicleVin?: string | null;
    };
  }
}

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:service-config-provider': GfaServiceConfigProviderV1;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 'page-info-service'?: PageInfoServiceV1;
  readonly 's2:server-request'?: ServerRequestV1;
  readonly 'audi-content-service'?: ContentService;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'audi-content-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
    },
  },
  optionalDependencies: {
    featureServices: {
      'gfa:service-config-provider': '^1.0.0',
      'audi-tracking-service': '^2.2.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:server-request': '^1.0.0',
      'page-info-service': '^1.0.0',
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');

    const pageInfoService = featureServices['page-info-service'];
    const serverRequest = featureServices['s2:server-request'];
    const contentService = featureServices['audi-content-service'];

    const asyncSSRManager = featureServices['s2:async-ssr-manager'];
    let ssrGraphQLData = null;
    let vehicleVin = null; // Variable to store the VIN

    // Helper function to normalize header values for fetch
    const normalizeHeader = (value: string | string[] | undefined): string | undefined => {
      if (!value) return undefined;
      return Array.isArray(value) ? value.join(', ') : value;
    };

    // Helper function to create common headers for SSR requests
    const createSSRHeaders = (correlationId: string): HeadersInit => ({
      'Content-Type': 'application/json',
      'apollographql-client-name': `${__FEATURE_APP_NAME__ || 'fa-vdp-header-section'}-ssr`,
      'apollographql-client-version': __FEATURE_APP_VERSION__,
      'x-correlation-id': correlationId,
      ...(serverRequest?.headers.host && {
        'x-forwarded-host': normalizeHeader(serverRequest.headers.host),
      }),
      ...(serverRequest?.headers['user-agent'] && {
        'user-agent': normalizeHeader(serverRequest.headers['user-agent']),
      }),
      ...(serverRequest?.headers.referer && {
        referer: normalizeHeader(serverRequest.headers.referer),
      }),
      ...(serverRequest?.headers['x-forwarded-for'] && {
        'x-forwarded-for': normalizeHeader(serverRequest.headers['x-forwarded-for']),
      }),
    });

    if (serverRequest) {
      const location = new URL(
        serverRequest.url,
        `https://${serverRequest.headers.host as string}`,
      );
      // take the raw query string for preserving the original order
      // if not returned from serverRequest, use the params from the website url as fallback
      const rawQueryString =
        (serverRequest.headers['raw-query-string'] as string | undefined) ?? location.search;
      const canonicalUrl = `${location.origin}${location.pathname}${
        rawQueryString ? `?${rawQueryString}` : ''
      }`;
      const market = location.searchParams.get('market');
      const vehicleId = location.searchParams.get('vehicleId');
      const isTier3Page = rawQueryString.includes('isdealer');
      const isValidVehicleId =
        !!vehicleId?.length && vehicleId.length > 17 && vehicleId.endsWith('=');
      const isVinNumber = !!vehicleId?.length && vehicleId.length < 18 && !vehicleId.endsWith('=');
      // const vin = location.searchParams.get('vin');
      const hostURL = location.origin;
      const path = location.pathname;

      const lang = 'en';
      const QUERY_VERSION = '2.0.0';
      const gateway = 'https://api.audiusa.com/graphql';
      const correlationId = uuidv4();

      // Skip redirect logic if we already have a VIN parameter
      // But don't return early - continue to the component rendering
      if (market && vehicleId) {
        // Only perform redirects if we don't already have a VIN
        if (asyncSSRManager && isValidVehicleId) {
          // Create a Promise directly and pass it to scheduleRerender
          const fetchPromise = (async (): Promise<GetVehicleInfoForWormwoodResponse | null> => {
            try {
              const response = await fetch(gateway, {
                method: 'POST',
                headers: createSSRHeaders(correlationId),
                body: JSON.stringify({
                  query: `
                    query getVehicleInfoForWormwood(
                      $market: MarketType!
                      $lang: String!
                      $id: String!
                      $version: String
                    ) {
                      getVehicleInfoForWormwood(market: $market, lang: $lang, id: $id, version: $version) {
                        vin
                      }
                    }
                  `,
                  variables: {
                    version: QUERY_VERSION,
                    market: market.toUpperCase(),
                    lang,
                    id: vehicleId,
                  },
                }),
              });

              const data = (await response.json()) as GetVehicleInfoForWormwoodResponse;

              ssrGraphQLData = {
                success: true,
                data,
                timestamp: Date.now(),
              };

              // Extract the VIN from the response
              if (data.data?.getVehicleInfoForWormwood?.vin) {
                vehicleVin = data.data.getVehicleInfoForWormwood.vin;
                const newCanonicalUrl = `${location.origin}${location.pathname}?${isTier3Page ? 'isdealer&' : ''}&market=${market}&vehicleId=${vehicleVin}`;

                // Set pageInfo with the VIN in the redirect URL
                pageInfoService?.setPageInfo({
                  seo: {
                    canonicalUrl: newCanonicalUrl,
                  },
                  response: {
                    status: 301,
                    message: 'Redirect',
                    headers: {
                      location: `${hostURL}${path}?${isTier3Page ? 'isdealer&' : ''}market=${market}&vehicleId=${vehicleVin}`,
                      'x-redirect-with-query': 'false',
                    },
                  },
                });
                // Fallback if VIN not found
              } else {
                pageInfoService?.setPageInfo({
                  seo: {
                    canonicalUrl,
                  },
                  response: {
                    status: 404,
                    message: 'No Vehicle Found',
                    headers: {},
                  },
                });
              }

              // Use type assertion to avoid TypeScript error
              if (typeof window !== 'undefined') {
                window.__SSR_GRAPHQL_DATA__ = {
                  ...ssrGraphQLData,
                  vehicleVin, // Include the VIN in the debug data
                };
              }

              return data;
            } catch (error) {
              console.log(error);

              ssrGraphQLData = {
                success: false,
                error: String(error),
                timestamp: Date.now(),
              };

              // Use type assertion to avoid TypeScript error
              if (typeof window !== 'undefined') {
                window.__SSR_GRAPHQL_DATA__ = ssrGraphQLData;
              }
              // Fallback redirect on error
              pageInfoService?.setPageInfo({
                seo: {
                  canonicalUrl,
                },
                response: {
                  status: 404,
                  message: 'No Vehicle Found',
                  headers: {},
                },
              });

              return null;
            }
          })();

          // Pass the Promise to scheduleRerender
          asyncSSRManager.scheduleRerender(fetchPromise);
        } else if (asyncSSRManager && isVinNumber) {
          // IF VIN is available
          // Create a Promise directly and pass it to scheduleRerender
          const fetchPromise = (async () => {
            try {
              const response = await fetch(gateway, {
                method: 'POST',
                headers: createSSRHeaders(correlationId),
                body: JSON.stringify({
                  query: `
                    query getFilteredVehiclesForWormwood(
                      $version: String
                      $market: [MarketType]!
                      $limit: Int
                      $lang: String!
                      $filters: String
                      $ranges: String
                      $sort: String
                      $offset: Int
                      $preset: String
                    ) {
                      getFilteredVehiclesForWormwood(
                        version: $version
                        market: $market
                        size: $limit
                        lang: $lang
                        filters: $filters
                        ranges: $ranges
                        sort: $sort
                        from: $offset
                        preset: $preset
                      ) {
                        filterResults {
                          totalCount
                        }
                        vehicles {
                          vin
                          id
                        }
                      }
                    }
                  `,
                  variables: {
                    version: QUERY_VERSION,
                    market: [market.toUpperCase()],
                    lang: 'en',
                    filters: `t_vin.${vehicleId}`,
                    ranges: '',
                    limit: 1,
                    offset: 0,
                    preset: '',
                  },
                }),
              });

              const data = (await response.json()) as GetFilteredVehiclesForWormwoodResponse;
              const vehicles = data.data?.getFilteredVehiclesForWormwood?.vehicles;
              const totalCount =
                data.data?.getFilteredVehiclesForWormwood?.filterResults?.totalCount ?? 0;

              ssrGraphQLData = {
                success: true,
                data,
                timestamp: Date.now(),
              };

              // If there is no data or there are more vehicles found then something is wrong so set 404
              if (!vehicles || totalCount === 0 || totalCount > 5) {
                // Set 404 response
                pageInfoService?.setPageInfo({
                  seo: {
                    canonicalUrl,
                  },
                  response: {
                    status: 404,
                    message: 'No Vehicle Found',
                    headers: {},
                  },
                });
              } else {
                // Fallback if data is found
                pageInfoService?.setPageInfo({
                  seo: {
                    canonicalUrl,
                  },
                });
              }

              // Use type assertion to avoid TypeScript error
              if (typeof window !== 'undefined') {
                window.__SSR_GRAPHQL_DATA__ = {
                  ...ssrGraphQLData,
                  vehicleVin, // Include the VIN in the debug data
                };
              }

              return data;
            } catch (error) {
              console.log(error);

              ssrGraphQLData = {
                success: false,
                error: String(error),
                timestamp: Date.now(),
              };

              // Use type assertion to avoid TypeScript error
              if (typeof window !== 'undefined') {
                window.__SSR_GRAPHQL_DATA__ = ssrGraphQLData;
              }
              pageInfoService?.setPageInfo({
                seo: {
                  canonicalUrl,
                },
                response: {
                  status: 404,
                  message: 'No Vehicle Found',
                  headers: {},
                },
              });

              return null;
            }
          })();

          // Pass the Promise to scheduleRerender
          asyncSSRManager.scheduleRerender(fetchPromise);
        }
      } else if (!(market && vehicleId)) {
        // if There is no market and vehicle ID in Query Params
        pageInfoService?.setPageInfo({
          seo: {
            canonicalUrl,
          },
        });
      }
      // Note: no early return here, we continue to set up the render function
    }

    const configProvider = featureServices['gfa:service-config-provider'];
    const trackingService = featureServices['audi-tracking-service'];
    const helmetContext = {};

    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    return {
      render: () => {
        return (
          <HelmetProvider context={helmetContext}>
            <App
              configProvider={configProvider}
              trackingService={trackingService}
              contentService={contentService}
            />
          </HelmetProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
